import { useState, useRef, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Lead, CONNECTOR_TYPE_LABELS, LOAN_TYPE_LABELS, LoanType, EmploymentType } from "@/types";
import { INDIAN_CITIES, WORLD_COUNTRIES, INDIA_PHONE_INFO, getCountryPhoneInfo } from "@/lib/constants";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useMobileFormScroll } from "@/hooks/useFormScroll";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { useBankers } from "@/hooks/useBankers";
import { useAppConfig } from "@/hooks/useAppConfig";
import { User, CreditCard, Home, Briefcase, FileCheck, Building, Loader2, AlertCircle, MapPin, Map, ExternalLink } from "lucide-react";
import { useModal } from "@/contexts/ModalContext";
import { editLeadSchema, validateFormData } from "@/lib/validations/leadValidation";
import { MapLocationPicker } from "@/components/common/MapLocationPicker";
import { 
  unformatPhoneNumber, 
  formatPANCard, 
  formatPincode, 
  formatCIBILScore,
  formatCurrencyInput,
} from "@/lib/utils/inputMasking";

interface EditLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave: (updatedLead: Partial<Lead>) => void | Promise<void>;
}

export function EditLeadDialog({ open, onOpenChange, lead, onSave }: EditLeadDialogProps) {
  const { partners: channelPartners = [] } = useChannelPartners();
  const { bankers, isFavorite: isBankerFavorite } = useBankers();
  const { config } = useAppConfig();
  const { registerModal, unregisterModal } = useModal();
  const [formData, setFormData] = useState({
    fullName: [lead.firstName, lead.lastName].filter(Boolean).join(' ') || (lead as { fullName?: string }).fullName || (lead as { name?: string }).name || "",
    mobile: lead.isNRI && lead.nriCountry
      ? (lead.mobile || '').replace(new RegExp(`^${getCountryPhoneInfo(lead.nriCountry).dialCode}`), '')
      : (lead.mobile || '').replace(/^\+91/, ''),
    secondaryNumber: lead.isNRI && lead.nriCountry
      ? (lead.secondaryNumber || '').replace(new RegExp(`^${getCountryPhoneInfo(lead.nriCountry).dialCode}`), '')
      : (lead.secondaryNumber || ''),
    email: lead.email || "",
    dateOfBirth: lead.dateOfBirth ? new Date(lead.dateOfBirth).toISOString().split('T')[0] : "",
    panCard: lead.panCard || "",
    pincode: lead.pincode || "",
    loanType: lead.loanType || "",
    loanAmount: lead.loanAmount?.toString() || "",
    loanTenure: lead.loanTenure?.toString() || "",
    lender: lead.lender || "",
    customerCity: lead.customerCity || "",
    employmentType: lead.employmentType || "",
    monthlySalary: lead.monthlySalary?.toString() || "",
    yearsInCurrentJob: lead.yearsInCurrentJob?.toString() || "",
    companyName: lead.companyName || "",
    cibilScore: lead.cibilScore?.toString() || "",
    leadSource: lead.leadSource || "",
    isBT: lead.isBT || false,
    isTopUp: lead.isTopUp || false,
    existingLender: lead.existingLender || "",
    outstandingAmount: lead.outstandingAmount?.toString() || "",
    currentInterestRate: lead.currentInterestRate?.toString() || "",
    remainingTenure: lead.remainingTenure?.toString() || "",
    topUpAmount: lead.topUpAmount?.toString() || "",
    connectorId: lead.connectorId || "",
    smContactId: lead.smContactId || "",
    // Property Details (Home Loan / LAP)
    propertyType: lead.propertyType || "",
    propertyValue: lead.propertyValue?.toString() || "",
    propertyPincode: lead.propertyPincode || "",
    propertyCity: lead.propertyCity || "",
    propertyAddress: lead.propertyAddress || "",
    // NRI / Residency
    isNRI: lead.isNRI || false,
    nriCountry: lead.nriCountry || "",
    // Property identification
    isPropertyIdentified: lead.isPropertyIdentified || false,
  });
  const [banks, setBanks] = useState<Array<{ id: string; name: string }>>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(lead.location || null);
  const formRef = useRef<HTMLDivElement>(null);

  useMobileFormScroll(formRef);

  // Register with ModalContext so Android back button closes this sheet
  useEffect(() => {
    if (!open) return;
    registerModal('edit-lead-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('edit-lead-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);

  // Convert config.banks to the format needed for the select
  useEffect(() => {
    if (config.banks && config.banks.length > 0) {
      const banksData = config.banks.map((bankName, index) => ({
        id: `bank-${index}`,
        name: bankName,
      }));
      setBanks(banksData);
    }
  }, [config.banks]);

  const updateField = (field: keyof typeof formData, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  // Mobile number prefix/length follows the selected NRI country; defaults to India.
  const phoneInfo = formData.isNRI
    ? getCountryPhoneInfo(formData.nriCountry as string)
    : INDIA_PHONE_INFO;

  // Trim mobile/secondary numbers down if switching to a country with a shorter number length
  useEffect(() => {
    setFormData((prev) => {
      const mobile = prev.mobile || "";
      const secondary = prev.secondaryNumber || "";
      if (mobile.length <= phoneInfo.maxLength && secondary.length <= phoneInfo.maxLength) return prev;
      return {
        ...prev,
        mobile: mobile.slice(0, phoneInfo.maxLength),
        secondaryNumber: secondary.slice(0, phoneInfo.maxLength),
      };
    });
  }, [phoneInfo.maxLength]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setValidationErrors({});

    // NRI validation: require country of residence
    if ((formData.isNRI as unknown as boolean) && !formData.nriCountry) {
      toast.error("Please select the country of residence for NRI customer");
      setIsSubmitting(false);
      return;
    }

    // Split fullName → firstName / lastName
    const fullNameRaw = (formData.fullName || '').trim();
    const nameParts = fullNameRaw.split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');

    // Prepare data for validation
    const dataToValidate = {
      ...formData,
      firstName,
      lastName,
      mobile: unformatPhoneNumber(formData.mobile),
      secondaryNumber: formData.secondaryNumber ? unformatPhoneNumber(formData.secondaryNumber) : '',
      assignedTo: lead.assignedTo,
      sharedWith: lead.sharedWith,
    };

    // Validate with Zod schema
    const validationResult = validateFormData(editLeadSchema, dataToValidate);
    
    if (!validationResult.success) {
      const errors = (validationResult as { success: false; errors: Record<string, string> }).errors;
      console.warn('[EditLeadDialog] Validation failed', {
        errors,
        data: dataToValidate,
      });
      setValidationErrors(errors);
      setIsSubmitting(false);
      toast.error("Please fix the validation errors");
      
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const validatedData = validationResult.data;

    // NRI numbers are stored with their country's calling code prefixed (no
    // "+"), same convention as CreateLeadSheet / Call/WhatsApp elsewhere.
    const dialCode = (formData.isNRI as unknown as boolean) ? getCountryPhoneInfo(formData.nriCountry).dialCode : '';

    try {
      await onSave({
        firstName: firstName,
        lastName: lastName || undefined,
        mobile: dialCode ? `${dialCode}${validatedData.mobile}` : validatedData.mobile!,
        secondaryNumber: validatedData.secondaryNumber
          ? (dialCode ? `${dialCode}${validatedData.secondaryNumber}` : validatedData.secondaryNumber)
          : undefined,
        email: validatedData.email || undefined,
        dateOfBirth: validatedData.dateOfBirth || undefined,
        panCard: validatedData.panCard || undefined,
        pincode: validatedData.pincode || undefined,
        loanType: validatedData.loanType as LoanType || undefined,
        loanAmount: validatedData.loanAmount,
        loanTenure: validatedData.loanTenure,
        lender: validatedData.lender || undefined,
        // customerCity handled below with NRI logic
        employmentType: validatedData.employmentType as EmploymentType | undefined,
        monthlySalary: validatedData.monthlySalary,
        cibilScore: validatedData.cibilScore,
        isBT: validatedData.isBT,
        isTopUp: validatedData.isTopUp,
        existingLender: validatedData.existingLender || undefined,
        outstandingAmount: validatedData.outstandingAmount,
        currentInterestRate: validatedData.currentInterestRate,
        remainingTenure: validatedData.remainingTenure,
        topUpAmount: validatedData.topUpAmount,
        sanctionedAmount: validatedData.sanctionedAmount,
        disbursedAmount: validatedData.disbursedAmount,
        payoutPercentage: validatedData.payoutPercentage,
        vendorPayoutPercentage: validatedData.vendorPayoutPercentage,
        companyName: validatedData.companyName || undefined,
        yearsInCurrentJob: validatedData.yearsInCurrentJob,
        leadSource: validatedData.leadSource as Lead['leadSource'] | undefined,
        connectorId: validatedData.connectorId || undefined,
        smContactId: validatedData.smContactId || undefined,
        location: location || undefined,
        // Property details (Home Loan / LAP)
        propertyType: formData.propertyType || undefined,
        propertyValue: formData.propertyValue ? Number(formData.propertyValue) : undefined,
        propertyPincode: formData.propertyPincode || undefined,
        propertyCity: (formData.isNRI as unknown as boolean) ? undefined : (formData.propertyCity || undefined),
        propertyAddress: formData.propertyAddress || undefined,
        // NRI / Residency
        isNRI: (formData.isNRI as unknown as boolean) || false,
        nriCountry: (formData.isNRI as unknown as boolean) ? (formData.nriCountry || undefined) : undefined,
        customerCity: (formData.isNRI as unknown as boolean) ? undefined : (validatedData.customerCity || undefined),
        // Property identification
        isPropertyIdentified: (formData.isPropertyIdentified as unknown as boolean) || false,
      });
      toast.success("Lead details updated");
      setValidationErrors({});
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error("Failed to save lead. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl p-0">
        <div className="flex flex-col h-full">
          <SheetHeader className="px-6 py-4 border-b shrink-0">
            <SheetTitle className="text-xl font-bold">Edit Lead Details</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-5 p-6 pt-4 pb-8" ref={formRef}>
          
          {/* SECTION 1: CUSTOMER BASICS */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-1">
              <User className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">Customer Information</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-sm font-medium">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="fullName"
                name="fullName"
                placeholder="Ex - Rajesh Kumar"
                value={formData.fullName}
                onChange={(e) => {
                  updateField("fullName", e.target.value);
                  if (validationErrors.firstName) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.firstName;
                      return newErrors;
                    });
                  }
                }}
                className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.firstName ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              />
              {validationErrors.firstName && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{validationErrors.firstName}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number <span className="text-red-500">*</span></Label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+{phoneInfo.dialCode || "--"}</span>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => {
                    updateField("mobile", e.target.value.replace(/\D/g, '').slice(0, phoneInfo.maxLength));
                    if (validationErrors.mobile) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.mobile;
                        return newErrors;
                      });
                    }
                  }}
                  maxLength={phoneInfo.maxLength}
                  className={`h-12 rounded-l-none placeholder:text-muted-foreground/50 ${validationErrors.mobile ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {validationErrors.mobile && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{validationErrors.mobile}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondaryNumber" className="text-sm font-medium">Alternate Number</Label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+{phoneInfo.dialCode || "--"}</span>
                <Input
                  id="secondaryNumber"
                  name="secondaryNumber"
                  type="tel"
                  placeholder={`Enter ${phoneInfo.minLength === phoneInfo.maxLength ? `${phoneInfo.maxLength}-digit` : `${phoneInfo.minLength}-${phoneInfo.maxLength} digit`} mobile number`}
                  value={formData.secondaryNumber}
                  onChange={(e) => {
                    updateField("secondaryNumber", e.target.value.replace(/\D/g, '').slice(0, phoneInfo.maxLength));
                    if (validationErrors.secondaryNumber) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.secondaryNumber;
                        return newErrors;
                      });
                    }
                  }}
                  maxLength={phoneInfo.maxLength}
                  className={`h-12 rounded-l-none placeholder:text-muted-foreground/50 ${validationErrors.secondaryNumber ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {validationErrors.secondaryNumber && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{validationErrors.secondaryNumber}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email (Optional)</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Ex - name@example.com"
                value={formData.email}
                onChange={(e) => {
                  updateField("email", e.target.value);
                  if (validationErrors.email) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                }}
                className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              />
              {validationErrors.email && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{validationErrors.email}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Date of Birth</Label>
              <Input 
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => updateField("dateOfBirth", e.target.value)}
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Location</Label>
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setLocationPickerOpen(true)}
                  >
                    <Map className="h-4 w-4 mr-2 shrink-0" />
                    {location ? "Location Set ✓ — Change" : "Update Location on Map"}
                  </Button>
                </div>
                {location && (
                  <p className="text-xs text-muted-foreground pl-1">📍 {location.lat.toFixed(6)}, {location.lng.toFixed(6)}</p>
                )}
              </div>
            </div>

            {/* NRI / Indian Resident Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label className="text-sm font-medium">Customer is NRI?</Label>
                  <p className="text-xs text-muted-foreground">Toggle ON if customer is Non-Resident Indian</p>
                </div>
                <Switch
                  checked={formData.isNRI as unknown as boolean}
                  onCheckedChange={(v) => updateField("isNRI", v)}
                />
              </div>
            </div>

            {/* Customer City + Pincode (Indian Resident only) */}
            {!formData.isNRI && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Customer City <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={INDIAN_CITIES.map(city => ({ value: city, label: city }))}
                  value={formData.customerCity}
                  onValueChange={(value) => {
                    updateField("customerCity", value);
                    if (validationErrors.customerCity) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.customerCity;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select city"
                  searchPlaceholder="Search cities..."
                  emptyText="No city found."
                  className="h-12"
                />
                {validationErrors.customerCity && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.customerCity}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Customer Pincode</Label>
                <Input 
                  name="pincode"
                  placeholder="Ex - 400001"
                  maxLength={6}
                  inputMode="numeric"
                  value={formData.pincode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    updateField("pincode", digits);
                    if (validationErrors.pincode) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.pincode;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.pincode ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.pincode && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.pincode}</span>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* NRI Country Picker (NRI only) */}
            {formData.isNRI as unknown as boolean && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Country of Residence <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={WORLD_COUNTRIES.map(c => ({ value: c, label: c }))}
                  value={formData.nriCountry}
                  onValueChange={(value) => updateField("nriCountry", value)}
                  placeholder="Select country"
                  searchPlaceholder="Search countries..."
                  emptyText="No country found."
                  className="h-12"
                />
              </div>
            )}

          </div>{/* end Customer Information section */}

          {/* SECTION 2: LOAN REQUIREMENT */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 pb-1">
              <CreditCard className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">Loan Requirement</h3>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Loan Type <span className="text-red-500">*</span></Label>
              <Select value={formData.loanType} onValueChange={(v) => updateField("loanType", v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LOAN_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="loanAmount" className="text-sm font-medium">Loan Amount (₹) <span className="text-red-500">*</span></Label>
                <Input
                  id="loanAmount"
                  name="loanAmount"
                  type="text"
                  inputMode="numeric"
                  placeholder="Ex - 50,00,000"
                  value={formatCurrencyInput(formData.loanAmount)}
                  onChange={(e) => {
                    updateField("loanAmount", e.target.value.replace(/,/g, ''));
                    if (validationErrors.loanAmount) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.loanAmount;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.loanAmount ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.loanAmount && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.loanAmount}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="loanTenure" className="text-sm font-medium">Tenure (Years) <span className="text-red-500">*</span></Label>
                <Input
                  id="loanTenure"
                  name="loanTenure"
                  type="number"
                  placeholder="Ex - 20"
                  value={formData.loanTenure}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  onChange={(e) => {
                    updateField("loanTenure", e.target.value);
                    if (validationErrors.loanTenure) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.loanTenure;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.loanTenure ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.loanTenure && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.loanTenure}</span>
                  </div>
                )}
              </div>
            </div>

            {/* BT & Top-Up Toggles */}
            <div className="grid grid-cols-2 gap-3">
              <div 
                className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                style={{
                  backgroundColor: formData.isBT ? 'rgb(254 252 232)' : 'transparent',
                  borderColor: formData.isBT ? 'rgb(251 191 36)' : 'rgb(226 232 240)'
                }}
                onClick={() => updateField("isBT", !formData.isBT)}
              >
                <Checkbox 
                  id="isBT"
                  checked={formData.isBT}
                  onCheckedChange={(checked) => updateField("isBT", checked)}
                />
                <div className="flex-1">
                  <Label htmlFor="isBT" className="text-sm font-semibold cursor-pointer block">
                    BT Case
                  </Label>
                  <p className="text-xs text-muted-foreground">Balance Transfer</p>
                </div>
              </div>
              
              <div 
                className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                style={{
                  backgroundColor: formData.isTopUp ? 'rgb(240 253 244)' : 'transparent',
                  borderColor: formData.isTopUp ? 'rgb(34 197 94)' : 'rgb(226 232 240)'
                }}
                onClick={() => updateField("isTopUp", !formData.isTopUp)}
              >
                <Checkbox 
                  id="isTopUp"
                  checked={formData.isTopUp}
                  onCheckedChange={(checked) => updateField("isTopUp", checked)}
                />
                <div className="flex-1">
                  <Label htmlFor="isTopUp" className="text-sm font-semibold cursor-pointer block">
                    Top-Up Case
                  </Label>
                  <p className="text-xs text-muted-foreground">Additional loan</p>
                </div>
              </div>
            </div>

            {/* BT Case Details */}
            {formData.isBT && (
              <div className="p-4 border-2 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-1 w-1 rounded-full bg-amber-600"></div>
                  <Label className="text-sm font-semibold text-amber-900">Existing Loan Details</Label>
                </div>
                <div className="space-y-3">
                  <div className="space-y-2" data-field="existingLender">
                    <Label className="text-xs font-medium text-amber-800">Current Lender</Label>
                    <SearchableSelect
                      options={banks.map(bank => ({ value: bank.name, label: bank.name }))}
                      value={formData.existingLender}
                      onValueChange={(v) => {
                        updateField("existingLender", v);
                        if (validationErrors.existingLender) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.existingLender;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Select current lender"
                      searchPlaceholder="Search lender..."
                      className={validationErrors.existingLender ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {validationErrors.existingLender && (
                      <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                        <AlertCircle className="h-3 w-3" />
                        <span>{validationErrors.existingLender}</span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-amber-800">Outstanding (₹)</Label>
                      <Input 
                        type="text"
                        inputMode="numeric"
                        placeholder="Ex - 25,00,000"
                        value={formatCurrencyInput(formData.outstandingAmount)}
                        name="outstandingAmount"
                        onChange={(e) => {
                          updateField("outstandingAmount", e.target.value.replace(/,/g, ''));
                          if (validationErrors.outstandingAmount) {
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.outstandingAmount;
                              return newErrors;
                            });
                          }
                        }}
                        className={`h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50 ${validationErrors.outstandingAmount ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      />
                      {validationErrors.outstandingAmount && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{validationErrors.outstandingAmount}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-amber-800">Current ROI (%)</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        placeholder="Ex - 9.5"
                        value={formData.currentInterestRate}
                        name="currentInterestRate"
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onChange={(e) => {
                          updateField("currentInterestRate", e.target.value);
                          if (validationErrors.currentInterestRate) {
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.currentInterestRate;
                              return newErrors;
                            });
                          }
                        }}
                        className={`h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50 ${validationErrors.currentInterestRate ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      />
                      {validationErrors.currentInterestRate && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{validationErrors.currentInterestRate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-amber-800">Remaining Tenure (Years)</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      placeholder="Ex - 15"
                      value={formData.remainingTenure}
                      onWheel={(e) => (e.target as HTMLElement).blur()}
                      onChange={(e) => updateField("remainingTenure", e.target.value)}
                      className="h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Top-Up Amount */}
            {formData.isTopUp && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Top-Up Amount (₹)</Label>
                <Input 
                  type="text"
                  inputMode="numeric"
                  placeholder="Ex - 5,00,000"
                  value={formatCurrencyInput(formData.topUpAmount)}
                  name="topUpAmount"
                  onChange={(e) => {
                    updateField("topUpAmount", e.target.value.replace(/,/g, ''));
                    if (validationErrors.topUpAmount) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.topUpAmount;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.topUpAmount ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.topUpAmount && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.topUpAmount}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SECTION 3: PROPERTY DETAILS */}
          {(['home_loan', 'lap'].includes(formData.loanType)) && (
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 pb-1">
                <Home className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Property Details</h3>
              </div>

              {/* Property Identified Toggle */}
              <div className="flex items-center justify-between py-1 bg-muted/30 rounded-lg px-3">
                <div>
                  <Label className="text-sm font-medium">Is Property Identified?</Label>
                  <p className="text-xs text-muted-foreground">Turn ON if the property has been shortlisted</p>
                </div>
                <Switch
                  checked={formData.isPropertyIdentified as unknown as boolean}
                  onCheckedChange={(v) => updateField("isPropertyIdentified", v)}
                />
              </div>

              {/* Rest of property fields — only shown when property is identified */}
              {(formData.isPropertyIdentified as unknown as boolean) && (
              <>

              {/* Property Type */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property Type</Label>
                <Select value={formData.propertyType} onValueChange={(v) => updateField("propertyType", v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment / Flat</SelectItem>
                    <SelectItem value="independent_house">Independent House / Villa</SelectItem>
                    <SelectItem value="plot">Plot / Land</SelectItem>
                    <SelectItem value="commercial">Commercial Property</SelectItem>
                    <SelectItem value="under_construction">Under Construction</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Property Value + Property Pincode */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Value (₹)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Ex - 75,00,000"
                    value={formatCurrencyInput(formData.propertyValue)}
                    onChange={(e) => updateField("propertyValue", e.target.value.replace(/,/g, ''))}
                    className="h-12 placeholder:text-muted-foreground/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Pincode</Label>
                  <Input
                    placeholder="Ex - 400001"
                    maxLength={6}
                    inputMode="numeric"
                    value={formData.propertyPincode}
                    onChange={(e) => updateField("propertyPincode", e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 placeholder:text-muted-foreground/50"
                  />
                </div>
              </div>

              {/* Property City */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property City</Label>
                <SearchableSelect
                  options={INDIAN_CITIES.map(city => ({ value: city, label: city }))}
                  value={formData.propertyCity}
                  onValueChange={(v) => updateField("propertyCity", v)}
                  placeholder="Select city"
                  searchPlaceholder="Search cities..."
                  emptyText="No city found."
                  className="h-12"
                />
              </div>

              {/* Property Address */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property Address</Label>
                <Input
                  placeholder="Building, street, area…"
                  value={formData.propertyAddress}
                  onChange={(e) => updateField("propertyAddress", e.target.value)}
                  className="h-12 placeholder:text-muted-foreground/50"
                />
              </div>
              </>
              )}
            </div>
          )}

          {/* SECTION 4: EMPLOYMENT & INCOME */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 pb-1">
              <Briefcase className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">Employment & Income</h3>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Employment Type</Label>
              <Select value={formData.employmentType} onValueChange={(v) => updateField("employmentType", v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salaried">Salaried</SelectItem>
                  <SelectItem value="self_employed_itr">Self Employed (ITR)</SelectItem>
                  <SelectItem value="self_employed_pro">Self Employed Professional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Monthly Income</Label>
                <Input 
                  name="monthlySalary"
                  type="text" 
                  inputMode="numeric"
                  placeholder="Ex - 50,000"
                  value={formatCurrencyInput(formData.monthlySalary)}
                  onChange={(e) => {
                    updateField("monthlySalary", e.target.value.replace(/,/g, ''));
                    if (validationErrors.monthlySalary) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.monthlySalary;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.monthlySalary ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.monthlySalary && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.monthlySalary}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Years in Job/Business</Label>
                <Input 
                  name="yearsInCurrentJob"
                  type="number"
                  step="0.5"
                  placeholder="Ex - 2.5"
                  value={formData.yearsInCurrentJob}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  onChange={(e) => {
                    updateField("yearsInCurrentJob", e.target.value);
                    if (validationErrors.yearsInCurrentJob) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.yearsInCurrentJob;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.yearsInCurrentJob ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.yearsInCurrentJob && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.yearsInCurrentJob}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Company / Business Name</Label>
              <Input 
                placeholder="Ex - TCS / Sharma Enterprises"
                value={formData.companyName}
                onChange={(e) => updateField("companyName", e.target.value)}
                className="h-12 placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          {/* SECTION 5: CREDIT PROFILE */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 pb-1">
              <FileCheck className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">Credit Profile</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">PAN Card</Label>
                <Input 
                  name="panCard"
                  placeholder="Ex - ABCDE1234F"
                  maxLength={10}
                  value={formatPANCard(formData.panCard)}
                  onChange={(e) => {
                    const formatted = formatPANCard(e.target.value);
                    updateField("panCard", formatted);
                    if (validationErrors.panCard) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.panCard;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.panCard ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.panCard && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.panCard}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">CIBIL Score</Label>
                <Input 
                  name="cibilScore"
                  placeholder="Ex - 750"
                  maxLength={3}
                  value={formData.cibilScore}
                  onChange={(e) => {
                    const formatted = formatCIBILScore(e.target.value);
                    updateField("cibilScore", formatted);
                    if (validationErrors.cibilScore) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.cibilScore;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.cibilScore ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.cibilScore && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.cibilScore}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 6: BANK & PARTNERSHIP */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 pb-1">
              <Building className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">Processing Details</h3>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Preferred Lender</Label>
              <SearchableSelect
                options={[
                  ...banks.map(bank => ({ value: bank.name, label: bank.name })),
                  ...(formData.lender && !banks.some(b => b.name === formData.lender)
                    ? [{ value: formData.lender, label: formData.lender }]
                    : [])
                ]}
                value={formData.lender}
                onValueChange={(v) => updateField("lender", v)}
                placeholder="Select lender"
                searchPlaceholder="Search banks..."
                emptyText="No bank found."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Lead Source</Label>
              <Select value={formData.leadSource} onValueChange={(v) => updateField("leadSource", v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="How did this lead come?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direct (Walk-in)</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="social_media">Social Media</SelectItem>
                  <SelectItem value="channel_partner">Channel Partner</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Channel Partner (Optional)</Label>
              <SearchableSelect
                options={[
                  { value: "none", label: "None" },
                  ...[...channelPartners]
                    .sort((a, b) => {
                      if (a.isFavorite && !b.isFavorite) return -1;
                      if (!a.isFavorite && b.isFavorite) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(partner => ({
                      value: partner.id,
                      label: `${partner.isFavorite ? '★ ' : ''}${partner.name} (${CONNECTOR_TYPE_LABELS[partner.type]}) - ${partner.city}`
                    }))
                ]}
                value={formData.connectorId || ""}
                onValueChange={(v) => updateField("connectorId", v === "none" ? "" : v)}
                placeholder="Select channel partner (optional)"
                searchPlaceholder="Search partners..."
                emptyText="No partner found."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Banker Contact (Optional)</Label>
              <SearchableSelect
                options={[
                  { value: "none", label: "None" },
                  ...[...bankers]
                    .sort((a, b) => {
                      const aFav = isBankerFavorite(a.id);
                      const bFav = isBankerFavorite(b.id);
                      if (aFav && !bFav) return -1;
                      if (!aFav && bFav) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(banker => ({
                      value: banker.id,
                      label: `${isBankerFavorite(banker.id) ? '★ ' : ''}${banker.name} - ${banker.bank}`,
                      displayLabel: banker.name
                    }))
                ]}
                value={formData.smContactId || ""}
                onValueChange={(v) => updateField("smContactId", v === "none" ? "" : v)}
                placeholder="Select banker contact (optional)"
                searchPlaceholder="Search bankers..."
                emptyText="No banker found."
              />
            </div>
          </div>

          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="w-full h-12 rounded-full bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
            </div>
          </div>
        </div>
      </SheetContent>

      <MapLocationPicker
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        initialLocation={location}
        onLocationSelect={(loc) => {
          setLocation(loc);
          setLocationPickerOpen(false);
        }}
      />
    </Sheet>
  );
}
